import { ProfilePictureLight } from '@/components/profile-picture/profile-picture.light';
import { SkeletonLight } from '@/components/skeleton/skeleton.light';
import { Loader, Warning } from '@nosinovacao/fe-icons';
import {
  Alert,
  Button,
  If,
  Toggle,
  useI18n,
  useImgSrcWithFallback,
} from '@nosinovacao/nosid-mfe-common';
import { type UserDetailProps } from '../-model/props';
import { type Application } from '@/portal-api.definition';

export function UserDetailLight({
  displayName,
  email,
  phone,
  pictureUri,
  isPending,
  applications,
  appsLoading,
  togglingAppId,
  deleteLoading,
  resendLoading,
  onToggleApp,
  onResendInvite,
  onDeleteUser,
}: Readonly<UserDetailProps>) {
  const { t } = useI18n();
  return (
    <div className="flex size-full flex-col gap-6 overflow-y-auto max-tablet:pb-8">
      {/* User info header */}
      <div className="flex items-center gap-4">
        <ProfilePictureLight name={displayName} src={pictureUri} size={56} />
        <div className="grid gap-0.5">
          <span
            className="font-bold text-nos-neutral-1000 font-title-h4"
            data-tid="text-display-name"
          >
            {displayName}
          </span>
          <If condition={!!email}>
            <span className="text-nos-neutral-900 font-text-p2" data-tid="text-email">
              {email}
            </span>
          </If>
          <If condition={!!phone}>
            <span className="text-nos-neutral-900 font-text-p2" data-tid="text-phone">
              {phone}
            </span>
          </If>
        </div>
      </div>

      {/* Pending invite tip */}
      <If condition={isPending}>
        <div className="flex flex-col gap-2 rounded-lg border-l-4 border-nos-neutral-400 bg-nos-neutral-75 p-4">
          <span
            className="font-medium text-nos-neutral-1000 font-text-p1"
            data-tid="text-pending-invite-title"
          >
            {t('Services.Users.Detail.PendingInvite.Title')}
          </span>
          <span className="text-nos-neutral-700 font-text-p2">
            {t('Services.Users.Detail.PendingInvite.Description', { name: displayName })}
          </span>
          <button
            type="button"
            onClick={onResendInvite}
            disabled={resendLoading}
            className="flex items-center gap-1 self-start font-medium text-nos-neutral-1000 underline font-text-p2 disabled:opacity-50"
            data-tid="button-resend-invite"
          >
            <If
              condition={resendLoading}
              otherwise={t('Services.Users.Detail.PendingInvite.Resend')}
            >
              <Loader width={16} height={16} />
            </If>
          </button>
        </div>
      </If>

      {/* App access control */}
      <div className="flex flex-col gap-4">
        <span className="text-nos-neutral-900 font-text-p1" data-tid="title-app-access">
          {t('Services.Users.Detail.AppAccessTitle')}
        </span>
        <SkeletonLight loading={appsLoading} className="h-16 rounded" count={5}>
          <div className="flex flex-col gap-2">
            {applications.map((app) => (
              <AppAccessCard
                key={app.Id}
                app={app}
                toggling={togglingAppId === app.Id}
                onToggle={(isEnabled) => onToggleApp(app.Id, isEnabled)}
              />
            ))}
          </div>
        </SkeletonLight>
      </div>

      {/* Remove user button */}
      <Alert
        skin="light"
        title={t('Services.Users.Detail.RemoveUserAlert.Title')}
        description={t('Services.Users.Detail.RemoveUserAlert.Description')}
        icon={<Warning width={48} height={48} className="text-nos-red" />}
        className="z-[70]"
        overlayClassName="z-[70]"
        data-tid="alert-remove-user"
        buttons={(close) => (
          <div className="mt-10 grid gap-4 tablet:grid-cols-2">
            <Button
              skin="light"
              destructive
              disabled={deleteLoading}
              data-tid="button-confirm-remove-user"
              onClick={async () => {
                await onDeleteUser();
                close();
              }}
            >
              <If condition={deleteLoading} otherwise={t('Services.Users.Detail.RemoveUser')}>
                <Loader width={24} height={24} />
              </If>
            </Button>
            <Button
              skin="light"
              variant="secondary"
              type="button"
              onClick={close}
              disabled={deleteLoading}
              className="max-tablet:row-start-1"
              data-tid="button-cancel-remove-user"
            >
              {t('Common.Cancel')}
            </Button>
          </div>
        )}
      >
        <Button
          className="mt-auto shrink-0"
          skin="light"
          variant="primary"
          type="button"
          destructive
          data-tid="button-remove-user"
        >
          {t('Services.Users.Detail.RemoveUser')}
        </Button>
      </Alert>
    </div>
  );
}

function AppAccessCard({
  app,
  toggling,
  onToggle,
}: {
  app: Application;
  toggling: boolean;
  onToggle: (isEnabled: boolean) => void;
}) {
  const inactive = !app.IsAuthorized && !app.CanRequestAccess;
  const { handleError, src } = useImgSrcWithFallback(
    app.LogoUrl,
    '/images/nos/default-app-image.svg',
  );
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-nos-neutral-200 bg-nos-neutral-0 p-4">
      <img
        className="size-8 rounded-lg border-[0.5px] border-nos-neutral-200 object-cover"
        alt={t('Apps.ApplicationIconAlt', { name: app.Name })}
        src={src}
        onError={handleError}
      />
      <span className="flex-1 font-medium text-nos-neutral-1000 font-title-h5">{app.Name}</span>
      <If
        condition={toggling}
        otherwise={
          <Toggle
            disabled={inactive}
            skin="light"
            checked={app.IsAuthorized}
            onChange={() => onToggle(!app.IsAuthorized)}
            data-tid={`toggle-app-${app.Id}`}
          />
        }
      >
        <Loader width={20} height={20} />
      </If>
    </div>
  );
}
